import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MODULES } from "@/data/lessons";
import { AccountTopBar, mono, serif } from "@/components/AccountLayout";
import { getMyCompletions } from "@/lib/progress.functions";
import { downloadMaterial, resolveDownload, type DownloadKind } from "@/lib/materials-download";


export const Route = createFileRoute("/my-courses_/basic-business-accountancy")({
  head: () => {
    const title = "Materials — The Basic Business Accountancy Course — WHO KNEW";
    const description =
      "All workbooks, templates, and downloads for The Basic Business Accountancy Course. Yours to keep.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/my-courses/basic-business-accountancy" },
      ],
    };
  },
  component: MaterialsPage,
});

type DownloadStatus = "idle" | "loading" | "error";
type DownloadState = { status: DownloadStatus; message?: string };

const MODULE_TITLES: Record<number, { eyebrow: string; title: string }> = {
  1: { eyebrow: "MODULE 01", title: "P&L" },
  2: { eyebrow: "MODULE 02", title: "Revenue" },
  3: { eyebrow: "MODULE 03", title: "Costs" },
  4: { eyebrow: "MODULE 04", title: "Balance Sheet and Cash Flow" },
  5: { eyebrow: "MODULE 05", title: "How Tricks and Traps Hide in Accounts" },
  6: { eyebrow: "MODULE 06", title: "Module 6 (full course materials)" },
};

function MaterialsPage() {
  const [completed, setCompleted] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getMyCompletions()
        .then((data) => {
          if (cancelled) return;
          if (data && typeof data === "object") {
            setCompleted(new Set(Object.keys(data)));
          }
        })
        .catch(() => {});
    };
    load();
    const onUpdate = () => load();
    window.addEventListener("wk-completion-updated", onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("wk-completion-updated", onUpdate);
    };
  }, []);

  const module6 = MODULES.find((m) => m.number === 6)!;
  const m6Done = module6.lessons.reduce((n, l) => n + (completed.has(l.id) ? 1 : 0), 0);
  const m6Total = module6.lessons.length;


  return (
    <div className="min-h-screen bg-paper text-ink">
      <AccountTopBar active="courses" />

      <main
        className="pt-[64px] mx-auto"
        style={{ maxWidth: "720px", paddingLeft: "24px", paddingRight: "24px" }}
      >
        <div style={{ paddingTop: "64px" }}>
          <div style={{ marginBottom: "16px" }}>
            <Link
              to="/my-courses"
              className="link-hit italic transition-colors hover:text-burgundy"
              style={{ ...serif, fontSize: "14px", color: "#5A534A" }}
            >
              Back to your courses
            </Link>
          </div>

          <p
            className="font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ ...mono, fontSize: "11px" }}
          >
            Materials
          </p>
          <h1
            className="text-[28px] md:text-[40px] font-bold leading-[1.15] text-ink"
            style={{ marginTop: "24px" }}
          >
            The Basic Business Accountancy Course
          </h1>
          <div style={{ marginTop: "24px", width: "60px", height: "2px", backgroundColor: "#8B1E1E" }} />

          <div style={{ marginTop: "48px" }}>
            {MODULES.filter((m) => m.number <= 5).map((mod) => {
              const done = mod.lessons.reduce((n, l) => n + (completed.has(l.id) ? 1 : 0), 0);
              const total = mod.lessons.length;
              const meta = MODULE_TITLES[mod.number];
              return (
                <ModuleSection
                  key={mod.number}
                  eyebrow={meta.eyebrow}
                  title={meta.title}
                  done={done}
                  total={total}
                  runtime={mod.totalRuntime}
                  moduleNumber={mod.number}
                />
              );
            })}

            <div>
              <p
                className="font-bold uppercase tracking-[0.05em] text-burgundy"
                style={{ ...mono, fontSize: "12px" }}
              >
                {MODULE_TITLES[6].eyebrow}
              </p>
              <h2
                className="font-bold text-ink"
                style={{ ...serif, fontSize: "24px", fontWeight: 700, marginTop: "8px", lineHeight: 1.2 }}
              >
                {MODULE_TITLES[6].title}
              </h2>
              <ProgressLine done={m6Done} total={m6Total} runtime="14:17" />
              <p
                className="text-[14px] italic"
                style={{ ...serif, color: "#5A534A", marginTop: "8px", lineHeight: 1.5 }}
              >
                Includes the full-course workbook and Excel template.
              </p>
              <div style={{ marginTop: "16px" }}>
                <DownloadButtons moduleNumber={6} padding={20} />
              </div>
            </div>
          </div>

          <p
            className="text-center text-[14px] italic"
            style={{ ...serif, color: "#5A534A", lineHeight: 1.5, marginTop: "48px", paddingBottom: "96px" }}
          >
            Yours forever. We'll add more here when new courses launch.
          </p>
        </div>
      </main>
    </div>
  );
}

function ProgressLine({ done, total, runtime }: { done: number; total: number; runtime: string }) {
  const isComplete = done === total;
  const isNotStarted = done === 0;
  const text = isComplete
    ? `COMPLETE · ${runtime} RUNTIME`
    : isNotStarted
      ? `NOT STARTED · ${runtime} RUNTIME`
      : `${done} OF ${total} LESSONS COMPLETE · ${runtime} RUNTIME`;
  return (
    <p
      className="font-bold uppercase tracking-[0.05em]"
      style={{
        ...mono,
        fontSize: "10px",
        marginTop: "8px",
        color: isComplete ? "#8B1E1E" : "#5A534A",
      }}
    >
      {text}
    </p>
  );
}

function ModuleSection({
  eyebrow,
  title,
  done,
  total,
  runtime,
  moduleNumber,
}: {
  eyebrow: string;
  title: string;
  done: number;
  total: number;
  runtime: string;
  moduleNumber: number;
}) {
  return (
    <div style={{ marginBottom: "40px" }}>
      <p
        className="font-bold uppercase tracking-[0.05em] text-burgundy"
        style={{ ...mono, fontSize: "12px" }}
      >
        {eyebrow}
      </p>
      <h2
        className="font-bold text-ink"
        style={{ ...serif, fontSize: "24px", fontWeight: 700, marginTop: "8px", lineHeight: 1.2 }}
      >
        {title}
      </h2>
      <ProgressLine done={done} total={total} runtime={runtime} />
      <div style={{ marginTop: "16px" }}>
        <DownloadButtons moduleNumber={moduleNumber} padding={16} />
      </div>
      <div style={{ marginTop: "40px", height: "1px", backgroundColor: "#E8E1D3" }} />
    </div>
  );
}

function DownloadButtons({ moduleNumber, padding }: { moduleNumber: number; padding: number }) {
  const wb = resolveDownload(moduleNumber, "workbook");
  const tpl = resolveDownload(moduleNumber, "template");
  const [wbState, setWbState] = useState<DownloadState>({ status: "idle" });
  const [tplState, setTplState] = useState<DownloadState>({ status: "idle" });

  const handleDownload = async (kind: DownloadKind) => {
    const setState = kind === "workbook" ? setWbState : setTplState;
    const meta = kind === "workbook" ? wb : tpl;
    setState({ status: "loading" });
    try {
      await downloadMaterial(meta.storageName, meta.displayName);
      setState({ status: "idle" });
    } catch (e) {
      setState({ status: "error", message: (e as Error)?.message ?? "Download failed. Try again." });
    }
  };

  const items = [
    { kind: "workbook" as const, meta: wb, state: wbState },
    { kind: "template" as const, meta: tpl, state: tplState },
  ];

  return (
    <div className="flex flex-col md:flex-row gap-3">
      {items.map(({ kind, meta, state }) => {
        const label = state.status === "loading" ? "DOWNLOADING…" : meta.label;
        return (
          <div key={kind} className="md:flex-1 flex flex-col">
            <button
              type="button"
              disabled={state.status === "loading"}
              onClick={() => handleDownload(kind)}
              className={`interactive-lift bg-paper-dark hover:bg-[#DDD4C2] text-ink text-[12px] font-bold uppercase tracking-[0.05em] rounded-none transition-colors ${state.status === "loading" ? "cursor-wait" : "cursor-pointer"}`}
              style={{ ...mono, padding: `${padding}px` }}
            >
              {label}
            </button>
            {state.status === "error" && state.message && (
              <p className="mt-2 text-[13px] italic" style={{ ...serif, color: "#5A534A" }}>
                {state.message}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
